import React, { useContext } from 'react';
import { useLoaderData } from 'react-router';
import { useTranslation } from 'react-i18next';
import PageTitle from '../components/PageTitle';
import ConfirmDetails from '../components/ConfirmDetails';
import { useStateSelector } from '../store/';
import useRenderLocationDetail from '../hooks/useRenderLocationDetail';
import { formatDate } from '../helpers/dateHelper';
import { getLocale } from '../helpers/localeHelper';
import { dateToCalendar } from '../services/appointment/convertedTime';
import AddToCalendarButton from '../components/AddToCalendarButton';
import { type AvailableService } from '../services/appointment/model';
import { type ServiceLocation } from '../helpers/appHelper';
import ConfirmDetailsWithStickyFooter from '../components/ConfirmDetailsWithStickyFooter';
import { type ConfirmPageLoaderData } from '../routers/loaders/confirmPageLoader';
import {
  selectSelectedTimeSlot,
  selectServiceForTheSelectedTimeSlot,
} from '../reducers/selectors';
import { ViewContext } from '../context';

export interface SharedConfirmDetailsProps {
  orderNo: string;
  service: AvailableService | undefined;
  serviceLocation: ServiceLocation | undefined;
  showPayOrder: boolean;
  timeText: string;
  addToCalendar: React.ReactNode;
  isLoading: boolean;
}

const ConfirmPage: React.FC = () => {
  const { t } = useTranslation();
  const view = useContext(ViewContext);
  const locale = getLocale();
  const { IANATimeZone } = useLoaderData<ConfirmPageLoaderData>();
  const orderNo = useStateSelector((state) => state.app.booking.orderNo);
  const payOrderLinkEnabled = useStateSelector(
    (state) => state.app.booking.payOrderLinkEnabled,
  );
  const service = useStateSelector(selectServiceForTheSelectedTimeSlot);
  const timeSlot = useStateSelector(selectSelectedTimeSlot);

  const serviceLocation = service?.location;
  const [
    {
      locationAddress,
      locationName,
      locationToCalendar,
      locationTypeIcon,
      locationTypeMessage,
    },
  ] = useRenderLocationDetail();

  const showPayOrder =
    payOrderLinkEnabled && !!service && service.customerPrice.price > 0;
  const timeText =
    timeSlot && service
      ? formatDate(
          timeSlot.dateFrom as string,
          timeSlot.dateTo as string,
          service.location,
          locale,
          t,
          CSS_CONFIG,
        )
      : '';

  const calendarDescription =
    serviceLocation && locationName
      ? `${locationName} - ${t(`services.name.${service?.serviceId}`)}`
      : `IKEA - ${t(`services.name.${service?.serviceId}`)}`;

  const addToCalendar =
    timeSlot && IANATimeZone ? (
      <AddToCalendarButton
        name={t(`services.name.${service?.serviceId}`)}
        description={calendarDescription}
        location={locationToCalendar}
        start={dateToCalendar(timeSlot.dateFrom, IANATimeZone)}
        end={dateToCalendar(timeSlot.dateTo, IANATimeZone)}
      />
    ) : null;

  const sharedProps = {
    orderNo,
    service,
    serviceLocation,
    showPayOrder,
    timeText,
    addToCalendar,
  };
  return (
    <>
      <PageTitle title={t('pages.confirm.pageTitle')} />
      {view === 'alt' || view === 'app' ? (
        <ConfirmDetailsWithStickyFooter
          {...sharedProps}
          view={view}
          locationTypeIcon={locationTypeIcon}
          locationTypeMessage={locationTypeMessage}
        />
      ) : (
        <ConfirmDetails
          {...sharedProps}
          view={view}
          locationAddress={locationAddress}
          locationName={locationName}
        />
      )}
    </>
  );
};

export default ConfirmPage;
